import { handleActions } from 'redux-actions';
import produce from 'immer';

import { Money, StaticSubsidyObject, Subsidy, SubsidyInfo, SubsidyTravellerParameters } from '@websky/graphql';
import {
	clearSelectedSubsidies,
	selectSubsidy,
	setCheckSubsidyResult,
	setCheckedTravellers,
	setSubsidiesError,
	setSubsidiesLoading,
	setSubsidiesResultsOpen,
	setSubsidiesState
} from './actions';

export interface SubsidiesInfoByPassengers {
	type: StaticSubsidyObject['type'];
	id: StaticSubsidyObject['id'];
	name: StaticSubsidyObject['name'];
	description: StaticSubsidyObject['description'];
	validation: StaticSubsidyObject['validation'];
}

export interface SubsidiesResultByPassengersSegments {
	segmentId: string;
	id: SubsidyInfo['subsidyId'];
	initialPrice: SubsidyInfo['initialPrice'];
	discount: SubsidyInfo['discount'];
	resultPrice: SubsidyInfo['resultPrice'];
	rejectMessage: SubsidyInfo['rejectMessage'];
}

export interface SubsidiesState {
	availableSubsidiesByPassengers: { [passengerId: string]: SubsidiesInfoByPassengers[] };
	subsidiesResultInitialPrice: Money;
	subsidiesResultByPassengersSegments: {
		[passengerId: string]: { [segmentId: string]: SubsidiesResultByPassengersSegments[] };
	};
	selectedSubsidies: {
		[passengerId: string]: Subsidy[];
	};
	selectSubsidiesError: string;
	isLoading: boolean;
	resultsOpen: boolean;
	checkedTravellers: SubsidyTravellerParameters[];
}

export const initialState: SubsidiesState = {
	selectSubsidiesError: null,
	subsidiesResultInitialPrice: null,
	availableSubsidiesByPassengers: {},
	subsidiesResultByPassengersSegments: {},
	selectedSubsidies: {},
	isLoading: false,
	resultsOpen: false,
	checkedTravellers: []
};

export default handleActions<SubsidiesState, any>(
	{
		[setSubsidiesState.toString()]: (state, { payload }: ReturnType<typeof setSubsidiesState>) => ({
			...payload
		}),

		[setSubsidiesLoading.toString()]: (state, { payload }: ReturnType<typeof setSubsidiesLoading>) => ({
			...state,
			isLoading: payload
		}),

		[setSubsidiesResultsOpen.toString()]: (state, { payload }: ReturnType<typeof setSubsidiesResultsOpen>) => ({
			...state,
			resultsOpen: payload
		}),

		[setCheckSubsidyResult.toString()]: (
			state: SubsidiesState,
			{ payload }: ReturnType<typeof setCheckSubsidyResult>
		) => {
			if (!payload) {
				return state;
			}

			return produce(state, draft => {
				const slice: SubsidiesState['subsidiesResultByPassengersSegments'] = {};
				draft.subsidiesResultInitialPrice = payload.initialOrderPrice;

				for (const segment of payload.segments) {
					for (const responsePassenger of segment.travellers) {
						const travellerId = responsePassenger.traveller.id;
						const segmentId = segment.segment.id;

						if (!slice[travellerId]) {
							slice[travellerId] = {};
						}

						if (!slice[travellerId][segmentId]) {
							slice[travellerId][segmentId] = [];
						}
					}
				}

				for (const segment of payload.segments) {
					for (const responsePassenger of segment.travellers) {
						slice[responsePassenger.traveller.id][segment.segment.id].push({
							segmentId: segment.segment.id,
							id: responsePassenger.subsidyInfo.subsidyId,
							discount: responsePassenger.subsidyInfo.discount,
							initialPrice: responsePassenger.subsidyInfo.initialPrice,
							rejectMessage: responsePassenger.subsidyInfo.rejectMessage,
							resultPrice: responsePassenger.subsidyInfo.resultPrice
						});

						draft.subsidiesResultByPassengersSegments = slice;
					}
				}
			});
		},

		[setSubsidiesError.toString()]: (state: SubsidiesState, { payload }: ReturnType<typeof setSubsidiesError>) => {
			return { ...state, selectSubsidiesError: payload };
		},

		[clearSelectedSubsidies.toString()]: (state: SubsidiesState) => {
			return produce(state, draft => {
				for (const [passengerId] of Object.entries(state.selectedSubsidies)) {
					draft.selectedSubsidies[passengerId] = [];
				}
			});
		},

		[selectSubsidy.toString()]: (state: SubsidiesState, { payload }: ReturnType<typeof selectSubsidy>) => {
			return produce(state, draft => {
				const payloadSubsidy: Subsidy = { subsidyId: payload.subsidyId, segmentId: payload.segmentId };

				const alreadySelectedSubsidyIndex = draft.selectedSubsidies[payload.passengerId].findIndex(
					alreadySelected =>
						alreadySelected.subsidyId === payloadSubsidy.subsidyId &&
						alreadySelected.segmentId === payloadSubsidy.segmentId
				);

				if (alreadySelectedSubsidyIndex !== -1) {
					draft.selectedSubsidies[payload.passengerId].splice(alreadySelectedSubsidyIndex, 1);
				} else {
					draft.selectedSubsidies[payload.passengerId].push(payloadSubsidy);
				}
			});
		},

		[setCheckedTravellers.toString()]: (
			state: SubsidiesState,
			{ payload }: ReturnType<typeof setCheckedTravellers>
		) => {
			return { ...state, checkedTravellers: payload };
		}
	},
	initialState
);
